import React from "react"
import type { IconProps } from "@tabler/icons-react"
import LandingReveal from "./LandingReveal"

type Pattern = "map" | "calendar" | "play" | "sparkle"

type Props = {
  variant?: "light" | "dark"
  icon: React.ComponentType<IconProps>
  title: string
  description: string
  action?: React.ReactNode
  pattern?: Pattern
}

const patternStyles: Record<Pattern, { light: string; dark: string }> = {
  map: {
    light:
      "bg-[radial-gradient(circle_at_30%_40%,rgba(0,75,214,0.06)_0%,transparent_50%),radial-gradient(circle_at_70%_60%,rgba(158,126,67,0.08)_0%,transparent_45%)]",
    dark: "bg-[radial-gradient(circle_at_30%_40%,rgba(214,229,255,0.06)_0%,transparent_50%),radial-gradient(circle_at_70%_60%,rgba(158,126,67,0.12)_0%,transparent_45%)]",
  },
  calendar: {
    light:
      "bg-[radial-gradient(circle_at_50%_30%,rgba(214,229,255,0.5)_0%,transparent_55%),radial-gradient(circle_at_80%_70%,rgba(158,126,67,0.06)_0%,transparent_40%)]",
    dark: "bg-[radial-gradient(circle_at_50%_30%,rgba(158,126,67,0.1)_0%,transparent_55%)]",
  },
  play: {
    light: "bg-[radial-gradient(circle_at_50%_50%,rgba(158,126,67,0.08)_0%,transparent_60%)]",
    dark: "bg-[radial-gradient(circle_at_50%_50%,rgba(158,126,67,0.15)_0%,transparent_55%)]",
  },
  sparkle: {
    light:
      "bg-[radial-gradient(circle_at_20%_30%,rgba(158,126,67,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_70%,rgba(45,26,69,0.05)_0%,transparent_45%)]",
    dark: "bg-[radial-gradient(circle_at_50%_50%,rgba(158,126,67,0.12)_0%,transparent_60%)]",
  },
}

const LandingEmptyState: React.FC<Props> = ({
  variant = "light",
  icon: Icon,
  title,
  description,
  action,
  pattern = "sparkle",
}) => {
  const isDark = variant === "dark"

  return (
    <LandingReveal delay={0.1} className="relative mx-auto w-full max-w-lg px-4">
      <div
        className={`relative overflow-hidden rounded-2xl border px-8 py-12 text-center ${
          isDark
            ? "border-white/10 bg-white/[0.03] shadow-[0_16px_48px_-16px_rgba(0,0,0,0.5)] backdrop-blur-sm"
            : "border-[#ebebeb]/80 bg-white shadow-[0_12px_40px_-16px_rgba(45,26,69,0.1)]"
        }`}
      >
        <div
          className={`pointer-events-none absolute inset-0 ${patternStyles[pattern][variant]}`}
          aria-hidden
        />

        {pattern === "map" && (
          <div className="pointer-events-none absolute inset-0 opacity-[0.35]" aria-hidden>
            <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="empty-map-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                  <path
                    d="M 32 0 L 0 0 0 32"
                    fill="none"
                    stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,75,214,0.08)"}
                    strokeWidth="1"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#empty-map-grid)" />
            </svg>
          </div>
        )}

        {pattern === "calendar" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-3 opacity-[0.12]" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-16 w-14 rounded-lg border-2 ${isDark ? "border-white/30" : "border-dc-primary-900/30"}`}
                style={{ transform: `rotate(${(i - 1) * 8}deg) translateY(${i === 1 ? -8 : 4}px)` }}
              />
            ))}
          </div>
        )}

        {pattern === "play" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
            {[120, 180, 240].map((size) => (
              <div
                key={size}
                className="absolute rounded-full border border-[#9e7e43]/10"
                style={{ width: size, height: size }}
              />
            ))}
          </div>
        )}

        <div className="relative flex flex-col items-center gap-5">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
              isDark
                ? "bg-[#9e7e43]/15 ring-1 ring-[#9e7e43]/25"
                : "bg-[#9e7e43]/10 ring-1 ring-[#9e7e43]/20"
            }`}
          >
            <Icon
              size={26}
              stroke={1.5}
              className={isDark ? "text-[#c9a55a]" : "text-[#9e7e43]"}
            />
          </div>

          <div className="flex flex-col gap-2">
            <h3
              className={`font-outfit text-xl font-semibold tracking-tight md:text-2xl ${
                isDark ? "text-white" : "text-[#2d1a45]"
              }`}
            >
              {title}
            </h3>
            <p
              className={`font-dm-sans text-sm leading-6 md:text-base ${
                isDark ? "text-white/60" : "text-[#5a5a5a]"
              }`}
            >
              {description}
            </p>
          </div>

          {action && <div className="pt-1">{action}</div>}
        </div>
      </div>
    </LandingReveal>
  )
}

export default LandingEmptyState
